import { useState, useEffect, useRef, useCallback } from "react";
import Peer from "simple-peer";
import { getSocket } from "../../../socket";
import {
  leaveConference,
  endConference,
  updateParticipantMedia,
  admitFromWaitingRoom,
  denyFromWaitingRoom,
  removeParticipant,
} from "../../../functions/call";
import {
  MuteMicIcon,
  MicrophoneIcon,
  VideoIcon,
  CameraOffIcon,
  EndCallIcon,
  ScreenShareIcon,
} from "../../../svg/chat";
import "./style.css";

// Additional icons
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

export default function ConferenceRoom({ conference, user, onClose }) {
  const [participants, setParticipants] = useState(conference.participants || []);
  const [waitingRoom, setWaitingRoom] = useState(conference.waitingRoom || []);
  const [isMuted, setIsMuted] = useState(conference.settings?.muteOnJoin || false);
  const [isVideoOff, setIsVideoOff] = useState(conference.settings?.videoOffOnJoin || false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [duration, setDuration] = useState(0);
  const [conferenceEnded, setConferenceEnded] = useState(false);

  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peersRef = useRef({});
  const timerRef = useRef(null);

  const isHost = conference.caller._id === user.id || conference.caller === user.id;
  const activeParticipants = participants.filter(p => p.status === "answered");

  // ICE servers
  const iceServers = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
    ],
  };

  // Initialize local media
  const initializeMedia = useCallback(async () => {
    try {
      const constraints = {
        audio: true,
        video: conference.type === "video",
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Apply initial mute/video settings
      if (isMuted) {
        stream.getAudioTracks().forEach(track => track.enabled = false);
      }
      if (isVideoOff && conference.type === "video") {
        stream.getVideoTracks().forEach(track => track.enabled = false);
      }

      return stream;
    } catch (error) {
      console.error("Error accessing media devices:", error);
      alert("Não foi possível acessar câmera/microfone.");
    }
  }, [conference.type, isMuted, isVideoOff]);

  // Create peer connection for a participant
  const createPeer = useCallback((participantId, initiator, stream) => {
    const socket = getSocket();
    if (!socket) return null;

    const peer = new Peer({
      initiator,
      trickle: false,
      stream,
      config: iceServers,
    });

    peer.on("signal", (signal) => {
      socket.emit("conference:signal", {
        conferenceId: conference._id,
        targetUserId: participantId,
        signal,
      });
    });

    peer.on("stream", (remoteStream) => {
      const videoElement = document.getElementById(`video-${participantId}`);
      if (videoElement) {
        videoElement.srcObject = remoteStream;
      }
    });

    peer.on("error", (err) => {
      console.error(`Peer error with ${participantId}:`, err);
    });

    return peer;
  }, [conference._id, iceServers]);

  // Handle socket events
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // User joined
    socket.on("conference:userJoined", ({ user: newUser }) => {
      setParticipants(prev => {
        const exists = prev.find(p =>
          (p.user._id || p.user) === (newUser._id || newUser)
        );
        if (!exists) {
          return [...prev, {
            user: newUser,
            joinedAt: new Date(),
            status: "answered",
            isVideoEnabled: true,
            isAudioEnabled: true,
          }];
        }
        return prev;
      });

      // Create peer connection
      if (localStreamRef.current) {
        const peer = createPeer(newUser._id, true, localStreamRef.current);
        if (peer) {
          peersRef.current[newUser._id] = peer;
        }
      }
    });

    // User left
    socket.on("conference:userLeft", ({ user: leftUser, conferenceEnded: ended }) => {
      setParticipants(prev =>
        prev.filter(p => (p.user._id || p.user) !== (leftUser._id || leftUser))
      );

      if (peersRef.current[leftUser._id]) {
        peersRef.current[leftUser._id].destroy();
        delete peersRef.current[leftUser._id];
      }

      if (ended) {
        setConferenceEnded(true);
      }
    });

    // Conference ended
    socket.on("conference:ended", () => {
      setConferenceEnded(true);
    });

    // User removed (you were removed)
    socket.on("conference:removed", () => {
      alert("Você foi removido da conferência pelo organizador.");
      handleLeave();
    });

    // Waiting room update
    socket.on("conference:waitingRoom", ({ user: waitingUser }) => {
      setWaitingRoom(prev => {
        const exists = prev.find(w =>
          (w.user._id || w.user) === (waitingUser._id || waitingUser)
        );
        if (!exists) {
          return [...prev, { user: waitingUser, requestedAt: new Date() }];
        }
        return prev;
      });
    });

    // Signal from other participant
    socket.on("conference:signal", ({ userId, signal }) => {
      if (peersRef.current[userId]) {
        peersRef.current[userId].signal(signal);
      } else if (localStreamRef.current) {
        const peer = createPeer(userId, false, localStreamRef.current);
        if (peer) {
          peersRef.current[userId] = peer;
          peer.signal(signal);
        }
      }
    });

    // Media update from other participant
    socket.on("conference:mediaUpdate", ({ userId, isVideoEnabled, isAudioEnabled }) => {
      setParticipants(prev => prev.map(p => {
        if ((p.user._id || p.user) === userId) {
          return { ...p, isVideoEnabled, isAudioEnabled };
        }
        return p;
      }));
    });

    return () => {
      socket.off("conference:userJoined");
      socket.off("conference:userLeft");
      socket.off("conference:ended");
      socket.off("conference:removed");
      socket.off("conference:waitingRoom");
      socket.off("conference:signal");
      socket.off("conference:mediaUpdate");
    };
  }, [createPeer]);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      const stream = await initializeMedia();
      if (!stream) return;

      // Create peer connections for existing participants
      activeParticipants.forEach(p => {
        const participantId = p.user._id || p.user;
        if (participantId !== user.id) {
          const peer = createPeer(participantId, true, stream);
          if (peer) {
            peersRef.current[participantId] = peer;
          }
        }
      });

      // Join socket room
      const socket = getSocket();
      if (socket) {
        socket.emit("conference:join", {
          conferenceId: conference._id,
          conferenceCode: conference.conferenceCode,
        });
      }
    };

    init();

    // Start duration timer
    timerRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);

    return () => {
      cleanup();
    };
  }, []);

  const cleanup = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
    }

    Object.values(peersRef.current).forEach(peer => peer.destroy());

    const socket = getSocket();
    if (socket) {
      socket.emit("conference:leave", { conferenceId: conference._id });
    }
  };

  const handleLeave = async () => {
    await leaveConference(conference._id, user.token);
    cleanup();
    onClose();
  };

  const handleEnd = async () => {
    if (!isHost) return;
    await endConference(conference._id, user.token);
    cleanup();
    onClose();
  };

  const toggleMute = async () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);

      await updateParticipantMedia(conference._id, {
        isAudioEnabled: isMuted,
      }, user.token);
    }
  };

  const toggleVideo = async () => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);

      await updateParticipantMedia(conference._id, {
        isVideoEnabled: isVideoOff,
      }, user.token);
    }
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }

      // Replace with camera stream
      if (localStreamRef.current && localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }

      setIsScreenSharing(false);
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
        screenStreamRef.current = screenStream;

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }

        // Replace video track in all peers
        const screenTrack = screenStream.getVideoTracks()[0];
        Object.values(peersRef.current).forEach(peer => {
          const sender = peer._pc?.getSenders().find(s => s.track?.kind === "video");
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
        });

        screenTrack.onended = () => {
          toggleScreenShare();
        };

        setIsScreenSharing(true);
      } catch (error) {
        console.error("Error sharing screen:", error);
      }
    }

    await updateParticipantMedia(conference._id, {
      isScreenSharing: !isScreenSharing,
    }, user.token);
  };

  const handleAdmit = async (visitorId) => {
    await admitFromWaitingRoom(conference._id, visitorId, user.token);
    setWaitingRoom(prev => prev.filter(w => (w.user._id || w.user) !== visitorId));
  };

  const handleDeny = async (visitorId) => {
    await denyFromWaitingRoom(conference._id, visitorId, user.token);
    setWaitingRoom(prev => prev.filter(w => (w.user._id || w.user) !== visitorId));
  };

  const handleRemove = async (participantId) => {
    await removeParticipant(conference._id, participantId, user.token);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(conference.conferenceCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Calculate grid layout based on participant count
  const getGridClass = () => {
    const count = activeParticipants.length;
    if (count <= 1) return "grid_1";
    if (count === 2) return "grid_2";
    if (count <= 4) return "grid_4";
    if (count <= 6) return "grid_6";
    return "grid_many";
  };

  if (conferenceEnded) {
    return (
      <div className="conference_room">
        <div className="conference_ended">
          <h2>Conferência Encerrada</h2>
          <p>A conferência foi encerrada pelo organizador.</p>
          <button onClick={onClose} className="btn_primary">
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="conference_room">
      {/* Header */}
      <div className="conference_header">
        <div className="conference_info">
          <h2>{conference.conferenceTitle || "Conferência"}</h2>
          <div className="conference_meta">
            <span className="conference_duration">{formatDuration(duration)}</span>
            <span className="conference_code" onClick={copyCode}>
              {conference.conferenceCode}
              {codeCopied ? <CheckIcon /> : <CopyIcon />}
            </span>
          </div>
        </div>

        <div className="conference_header_actions">
          {waitingRoom.length > 0 && isHost && (
            <button
              className="header_btn waiting_room_btn"
              onClick={() => setShowWaitingRoom(true)}
            >
              <span className="waiting_badge">{waitingRoom.length}</span>
              Sala de espera
            </button>
          )}
          <button
            className={`header_btn ${showParticipants ? "active" : ""}`}
            onClick={() => setShowParticipants(!showParticipants)}
          >
            <UsersIcon />
            <span>{activeParticipants.length}</span>
          </button>
          {isHost && (
            <button
              className={`header_btn ${showSettings ? "active" : ""}`}
              onClick={() => setShowSettings(!showSettings)}
            >
              <SettingsIcon />
            </button>
          )}
        </div>
      </div>

      {/* Video Grid */}
      <div className={`conference_grid ${getGridClass()}`}>
        {/* Local video */}
        <div className="participant_tile local">
          {conference.type === "video" ? (
            <video
              ref={localVideoRef}
              className={`participant_video ${isVideoOff ? "video_off" : ""}`}
              autoPlay
              playsInline
              muted
            />
          ) : (
            <div className="audio_avatar">
              <img src={user.picture} alt={user.first_name} />
            </div>
          )}
          <div className="participant_info">
            <span className="participant_name">Você {isHost ? "(Organizador)" : ""}</span>
            <div className="participant_status">
              {isMuted && <MuteMicIcon color="#fff" />}
              {isVideoOff && conference.type === "video" && <CameraOffIcon color="#fff" />}
              {isScreenSharing && <ScreenShareIcon color="#4caf50" />}
            </div>
          </div>
        </div>

        {/* Remote participants */}
        {activeParticipants
          .filter(p => (p.user._id || p.user) !== user.id)
          .map((participant) => {
            const pUser = participant.user;
            const pId = pUser._id || pUser;
            const isParticipantHost = pId === (conference.caller._id || conference.caller);

            return (
              <div key={pId} className="participant_tile">
                {conference.type === "video" ? (
                  <video
                    id={`video-${pId}`}
                    className={`participant_video ${!participant.isVideoEnabled ? "video_off" : ""}`}
                    autoPlay
                    playsInline
                  />
                ) : (
                  <div className="audio_avatar">
                    <img src={pUser.picture} alt={pUser.first_name} />
                  </div>
                )}
                {!participant.isVideoEnabled && conference.type === "video" && (
                  <div className="video_off_overlay">
                    <img src={pUser.picture} alt={pUser.first_name} />
                  </div>
                )}
                <div className="participant_info">
                  <span className="participant_name">
                    {pUser.first_name} {pUser.last_name} {isParticipantHost ? "(Organizador)" : ""}
                  </span>
                  <div className="participant_status">
                    {!participant.isAudioEnabled && <MuteMicIcon color="#fff" />}
                    {!participant.isVideoEnabled && conference.type === "video" && <CameraOffIcon color="#fff" />}
                    {participant.isScreenSharing && <ScreenShareIcon color="#4caf50" />}
                  </div>
                </div>
                {isHost && !isParticipantHost && (
                  <button
                    className="remove_participant"
                    onClick={() => handleRemove(pId)}
                    title="Remover participante"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            );
          })}
      </div>

      {/* Controls */}
      <div className="conference_controls">
        <button
          className={`control_btn ${isMuted ? "active" : ""}`}
          onClick={toggleMute}
          title={isMuted ? "Ativar microfone" : "Desativar microfone"}
        >
          {isMuted ? <MuteMicIcon color="#fff" /> : <MicrophoneIcon color="#fff" />}
        </button>

        {conference.type === "video" && (
          <button
            className={`control_btn ${isVideoOff ? "active" : ""}`}
            onClick={toggleVideo}
            title={isVideoOff ? "Ligar câmera" : "Desligar câmera"}
          >
            {isVideoOff ? <CameraOffIcon color="#fff" /> : <VideoIcon color="#fff" />}
          </button>
        )}

        {conference.settings?.allowScreenShare && (
          <button
            className={`control_btn ${isScreenSharing ? "screen_sharing" : ""}`}
            onClick={toggleScreenShare}
            title={isScreenSharing ? "Parar compartilhamento" : "Compartilhar tela"}
          >
            <ScreenShareIcon color="#fff" />
          </button>
        )}

        <button
          className="control_btn leave"
          onClick={handleLeave}
          title="Sair da conferência"
        >
          <EndCallIcon />
        </button>

        {isHost && (
          <button
            className="control_btn end_all"
            onClick={handleEnd}
            title="Encerrar para todos"
          >
            Encerrar
          </button>
        )}
      </div>

      {/* Participants Panel */}
      {showParticipants && (
        <div className="side_panel participants_panel">
          <div className="panel_header">
            <h3>Participantes ({activeParticipants.length})</h3>
            <button onClick={() => setShowParticipants(false)}>
              <CloseIcon />
            </button>
          </div>
          <div className="panel_content">
            {activeParticipants.map((p) => {
              const pUser = p.user;
              const pId = pUser._id || pUser;
              const isParticipantHost = pId === (conference.caller._id || conference.caller);

              return (
                <div key={pId} className="participant_item">
                  <img src={pUser.picture} alt={pUser.first_name} />
                  <div className="participant_item_info">
                    <span className="name">
                      {pUser.first_name} {pUser.last_name}
                      {pId === user.id && " (Você)"}
                    </span>
                    {isParticipantHost && <span className="host_badge">Organizador</span>}
                  </div>
                  <div className="participant_item_status">
                    {!p.isAudioEnabled && <MuteMicIcon color="#65676b" />}
                    {!p.isVideoEnabled && conference.type === "video" && <CameraOffIcon color="#65676b" />}
                  </div>
                  {isHost && !isParticipantHost && pId !== user.id && (
                    <button
                      className="remove_btn"
                      onClick={() => handleRemove(pId)}
                    >
                      Remover
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Waiting Room Panel */}
      {showWaitingRoom && isHost && (
        <div className="side_panel waiting_panel">
          <div className="panel_header">
            <h3>Sala de Espera ({waitingRoom.length})</h3>
            <button onClick={() => setShowWaitingRoom(false)}>
              <CloseIcon />
            </button>
          </div>
          <div className="panel_content">
            {waitingRoom.length === 0 ? (
              <div className="empty_waiting">
                <p>Nenhum participante esperando</p>
              </div>
            ) : (
              waitingRoom.map((w) => {
                const wUser = w.user;
                const wId = wUser._id || wUser;

                return (
                  <div key={wId} className="waiting_item">
                    <img src={wUser.picture} alt={wUser.first_name} />
                    <div className="waiting_item_info">
                      <span className="name">{wUser.first_name} {wUser.last_name}</span>
                      <span className="waiting_time">Aguardando...</span>
                    </div>
                    <div className="waiting_actions">
                      <button className="admit_btn" onClick={() => handleAdmit(wId)}>
                        Admitir
                      </button>
                      <button className="deny_btn" onClick={() => handleDeny(wId)}>
                        Recusar
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Conference Invite Modal Component
export function ConferenceInviteModal({ invite, onAccept, onDecline }) {
  return (
    <div className="conference_invite_modal">
      <div className="invite_content">
        <h3>Convite para Conferência</h3>
        <p>
          <strong>{invite.inviter?.first_name} {invite.inviter?.last_name}</strong> está
          convidando você para uma conferência de {invite.type === "video" ? "vídeo" : "voz"}.
        </p>
        <div className="invite_info">
          <span className="invite_title">{invite.conferenceTitle || "Conferência"}</span>
          <span className="invite_code">{invite.conferenceCode}</span>
        </div>
        <div className="invite_actions">
          <button className="decline_btn" onClick={onDecline}>
            Recusar
          </button>
          <button className="accept_btn" onClick={onAccept}>
            Entrar
          </button>
        </div>
      </div>
    </div>
  );
}
